/**
 * ProPoule Sound Engine 🔊
 * Uses Web Audio API to synthesize premium UI sounds without external assets.
 */

class SoundEngine {
    constructor() {
        this.ctx = null;
        this.enabled = true;
        this.initialized = false;
    }

    init() {
        if (!this.initialized && typeof window !== 'undefined') {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (AudioContext) {
                this.ctx = new AudioContext();
                this.initialized = true;
            }
        }
    }

    async playTone(freq, type, duration, vol = 0.1) {
        if (!this.enabled) return;

        // 1. Init if needed
        if (!this.ctx) {
            this.init();
            if (!this.ctx) return;
        }

        // 2. FORCE RESUME (Critical for autoplay policy)
        if (this.ctx.state === 'suspended') {
            try {
                await this.ctx.resume();
            } catch (err) {
                // Ignore resume errors, usually means user interaction needed
            }
        }

        const t = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = type;
        osc.frequency.setValueAtTime(freq, t);

        // Anti-pop envelope
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(vol, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

        osc.connect(gain);
        gain.connect(this.ctx.destination);

        osc.start(t);
        osc.stop(t + duration + 0.1);

        // Cleanup
        osc.onended = () => {
            gain.disconnect();
            osc.disconnect();
        }
    }

    play(soundType) {
        if (!this.enabled) return;

        switch (soundType) {
            case 'click':
                // Crisp click: High Sine, very short
                this.playTone(800, 'sine', 0.05, 0.05);
                break;

            case 'hover':
                // Very subtle ticks
                this.playTone(400, 'triangle', 0.02, 0.01);
                break;

            case 'select':
                // Satisfying bubble pop
                this.playTone(600, 'sine', 0.1, 0.1);
                setTimeout(() => this.playTone(1200, 'sine', 0.1, 0.05), 50);
                break;

            case 'success':
                // Major Chord Arpeggio (C Major)
                const now = this.ctx ? this.ctx.currentTime : 0;
                this.playTone(523.25, 'sine', 0.2, 0.1); // C5
                setTimeout(() => this.playTone(659.25, 'sine', 0.2, 0.1), 80); // E5
                setTimeout(() => this.playTone(783.99, 'sine', 0.4, 0.1), 160); // G5
                break;

            case 'navigate':
                // Low swoosh
                this.playTone(200, 'sine', 0.15, 0.05);
                break;

            case 'pop':
                this.playTone(800, 'sine', 0.1, 0.1);
                break;

            case 'focus':
                // Very subtle high air sound
                this.playTone(1500, 'sine', 0.05, 0.02);
                break;

            case 'error':
                // Low thud / buzz
                this.playTone(150, 'sawtooth', 0.2, 0.1);
                this.playTone(100, 'square', 0.2, 0.05);
                break;

            case 'warning':
                // Dissonant interval
                this.playTone(440, 'triangle', 0.3, 0.1);
                this.playTone(466, 'triangle', 0.3, 0.1); // Minor second
                break;

            case 'chord':
                // C Major 7 chord flourish
                [523.25, 659.25, 783.99, 987.77].forEach((freq, i) => {
                    setTimeout(() => this.playTone(freq, 'sine', 0.4, 0.05), i * 60);
                });
                break;
        }
    }

    toggle(enabled) {
        this.enabled = enabled;
    }
}

export const soundFx = new SoundEngine();
